import { Box } from '@mui/material';
import ProgressIndicator from './ProgressIndicator';
import { useEffect, useState } from 'react';
import { useInterfaceStore } from '@/store/interface';
import proxy from '@/services/ProxyService';
import api from '@/services/ApiService';
import {
  mergeParties,
  transformToInternalSchema,
} from '@/components/forms/party/partyManagerUtils';
import { useRouteLoaderData } from 'react-router-dom';

const PHASE_1_END = 20;
const PHASE_2_END = 40;
const PHASE_3_END = 95;

const PartyLoader = ({ setResult, sourceOrder = ['weclapp', 'tanss'] }) => {
  const { users } = useRouteLoaderData('auth');
  const [logs, setLogs] = useState([]);
  const [progress, setProgress] = useState(0);
  const [result, setLocalResult] = useState(null);

  // Retry wrapper with exponential backoff for transient errors (5xx and 429)
  async function retry(fn, retries = 3, base = 2000) {
    let attempt = 0;
    while (true) {
      try {
        return await fn();
      } catch (err) {
        attempt++;
        const status = err?.response?.status;
        // If not a server error or rate limit, don't retry
        if (attempt > retries || (status && status < 500 && status !== 429)) {
          throw err;
        }
        const wait = base * Math.pow(2, attempt - 1);
        log(`Transient error (status=${status || 'network'}). Retrying in ${wait}ms (attempt ${attempt}/${retries})`);
        await new Promise((r) => setTimeout(r, wait));
      }
    }
  }
  const INTERFACE_STORE = useInterfaceStore.getState();

  function log(message) {
    setLogs((prevLogs) => [...prevLogs, message]);
  }

  async function loadData() {
    let excludedIds = [];
    try {
      log('Lade Stammdaten...');
      setProgress(5);

      const tanssPromise = retry(() => proxy.put(`/${INTERFACE_STORE.tanss}/companies`, {}));
      const codemetaOrgsPromise = retry(() => api.get('/party?partyType=ORGANIZATION&limit=100000'));
      const codemetaPersonsPromise = retry(() => api.get('/party?partyType=PERSON&limit=100000'));

      const [
        tanssCompaniesResponse,
        codemetaOrganizationsResponse,
        codemetaPersonsResponse,
      ] = await Promise.all([
        tanssPromise,
        codemetaOrgsPromise,
        codemetaPersonsPromise,
      ]);

      // Load weclapp parties with pagination
      let done = false;
      let page = 1;
      const weclappPartiesResponse = [];
      while (!done) {
        const {
          data: { result: weclappParties },
        } = await retry(() => proxy.get(`/${INTERFACE_STORE.weclapp}/party?pageSize=1000&page=${page}`));
        page++;
        if (weclappParties.length < 1000) {
          done = true;
        }
        weclappPartiesResponse.push(...weclappParties);
      }

      const tanssCompanies = tanssCompaniesResponse.data.content;
      const weclappOrganizations = weclappPartiesResponse.filter(
        (p) =>
          p.partyType === 'ORGANIZATION' && p.company !== 'ANONYMOUS_COMPANY',
      );
      const weclappContacts = weclappPartiesResponse
        .filter(
          (p) => p.partyType === 'PERSON' && p.lastName !== 'ANONYMOUS_COMPANY',
        )
        .map(({ parentPartyId, ...rest }) => ({ ...rest })); // TODO: implement parentPartyId relation

      // Initial codemeta parties
      const initialCodemetaOrgs = codemetaOrganizationsResponse.data.result;
      const initialCodemetaPersons = codemetaPersonsResponse.data.result;

      // Enrich codemeta parties with datasources
      const [enrichedOrganizations, enrichedPersons] = await Promise.all([
        Promise.all(
          initialCodemetaOrgs?.map((org) =>
            retry(() => api.get(`/datasource?recordId=${org._id}`)).then(({ data: { result: data } }) => ({
              ...org,
              id: org._id,
              datasources: data?.map((ds) => ({
                source: ds.source,
                sourceId: ds.sourceId,
                recordId: ds.recordId,
                ...(ds.sourceType && { sourceType: ds.sourceType }),
              })),
            })),
          ),
        ),
        Promise.all(
          initialCodemetaPersons?.map((person) =>
            retry(() => api.get(`/datasource?recordId=${person._id}`)).then(({ data: { result: data } }) => ({
              ...person,
              id: person._id,
              datasources: data?.map((ds) => ({
                source: ds.source,
                sourceId: ds.sourceId,
                recordId: ds.recordId,
                ...(ds.sourceType && { sourceType: ds.sourceType }),
              })),
            })),
          ),
        ),
      ]);

      const weclappCustomers = weclappOrganizations.filter((o) => o.customer);
      const weclappSuppliers = weclappOrganizations.filter((o) => o.supplier);

      log(
        `Weclapp Firmen geladen: ${weclappOrganizations.length} (${weclappCustomers.length} Kunden/Interessenten, ${weclappSuppliers.length} Lieferanten)`,
      );
      log(`Weclapp Kontakte geladen: ${weclappContacts.length}`);
      setProgress(PHASE_1_END);

      const PHASE_2_CHUNK_SIZE = PHASE_2_END - PHASE_1_END;
      // Helper to rate limit async requests
      async function rateLimitedMap(array, asyncFn, limit = 5) {
        const results = [];
        let i = 0;
        async function next() {
          if (i >= array.length) return;
          const idx = i++;
          results[idx] = await asyncFn(array[idx], idx);
          await next();
        }
        await Promise.all(Array(Math.min(limit, array.length)).fill().map(next));
        return results;
      }

      const tanssEmployeeEntries = await rateLimitedMap(
        tanssCompanies,
        async (c, index) => {
          const {
            data: { content: employees },
          } = await retry(() => proxy.get(`/${INTERFACE_STORE.tanss}/companies/${c.id}/employees`));
          const currentProgress =
            PHASE_1_END +
            ((index + 1) / tanssCompanies.length) * PHASE_2_CHUNK_SIZE;
          setProgress(currentProgress);
          return [c.id, employees];
        },
        3 // concurrency limit
      );
      const tanssEmployeeMap = Object.fromEntries(tanssEmployeeEntries);
      log(`Tanss Firmen geladen: ${tanssCompanies.length}`);
      const allEmployees = Object.values(tanssEmployeeMap).flat();
      const uniqueEmployeeIds = new Set(allEmployees.map((emp) => emp.id));
      log(`Tanss Mitarbeiter geladen: ${uniqueEmployeeIds.size}`);
      setProgress(PHASE_2_END);

      let resultOrganizations = enrichedOrganizations?.map(
        ({ version, contacts, ...p }) => ({
          ...p,
          contacts:
            contacts?.map((c) => ({
              ...c,
              ...(enrichedPersons?.find((person) => person._id === c.id) || {}),
            })) || [],
        }),
      );
      let existingPersons = enrichedPersons?.map(({ version, ...p }) => ({
        ...p,
      }));

      const findParentOrg = (source, sourceParentId) => {
        return resultOrganizations.find((org) =>
          org.datasources?.some(
            (ds) => ds.source === source && String(ds.sourceId) == String(sourceParentId),
          ),
        );
      };

      const PROCESSING_CHUNK_SIZE = PHASE_3_END - PHASE_2_END;
      const totalProcessingItems =
        weclappOrganizations.length +
        weclappContacts.length +
        tanssCompanies.length +
        Object.values(tanssEmployeeMap).flat().length;
      let processedItems = 0;

      const updateProcessingProgress = () => {
        processedItems++;
        if (totalProcessingItems > 0) {
          setProgress(
            PHASE_2_END +
              (processedItems / totalProcessingItems) * PROCESSING_CHUNK_SIZE,
          );
        }
      };

      for (const source of sourceOrder) {
        log(`Verarbeite Daten aus ${source}...`);
        if (source === 'weclapp') {
          /*
           * Weclapp Organizations
           */
          for (const org of weclappOrganizations) {
            if (!org.customer && !org.supplier) {
              updateProcessingProgress();
              continue;
            }
            let existing = resultOrganizations.find(
              (item) =>
                (org.supplier && item.supplierNumber === org.supplierNumber) ||
                (org.customer && item.customerNumber === org.customerNumber),
            );
            if (org?.responsibleUserId) {
              org.responsibleUserId =
                users.find((u) => u.weclappId === org.responsibleUserId)?._id ||
                null;
            }
            const party = transformToInternalSchema('weclapp', org);
            if (!existing) {
              party.datasources = [
                { source: 'weclapp', sourceId: String(party.id) },
              ];
              party.contacts = [];
              resultOrganizations.push(party);
            } else {
              if (!existing.datasources) existing.datasources = [];
              if (
                !existing.datasources.some(
                  (ds) =>
                    ds.source === 'weclapp' && ds.sourceId === String(org.id),
                )
              ) {
                existing.datasources.push({
                  source: 'weclapp',
                  sourceId: String(org.id),
                });
              }
              if (!existing.contacts) existing.contacts = [];
              if (
                sourceOrder.filter((source) =>
                  existing.datasources.some((ds) => ds.source === source),
                )[0] === 'weclapp'
              ) {
                // Overwrite with Weclapp data if Weclapp is the first source
                party.contacts = [];
                const merged = mergeParties(existing, party);
                Object.assign(existing, merged);
              }
            }
            updateProcessingProgress();
          }
          /*
           * Weclapp Contacts
           */
          for (const contact of weclappContacts) {
            if (!contact.email) {
              updateProcessingProgress();
              continue;
            }
            // Get existing contact or personal customer by email
            let existing =
              resultOrganizations
                .map((o) => o.contacts)
                .flat()
                .find((p) => p.email === contact.email) ||
              existingPersons.find(
                (p) =>
                  p.email === contact.email &&
                  p.partyType === 'PERSON' &&
                  p.customer,
              );
            if (contact?.responsibleUserId) {
              contact.responsibleUserId =
                users.find((u) => u.weclappId === contact.responsibleUserId)
                  ?._id || null;
            }
            const party = transformToInternalSchema('weclapp', contact);
            const parentOrgData = weclappOrganizations.find((o) =>
              o.contacts?.some((c) => c.id === contact.id),
            );
            const parentOrg = findParentOrg('weclapp', parentOrgData?.id);
            if (!existing) {
              if (parentOrg) {
                // contact person for organization
                party.datasources = [
                  { source: 'weclapp', sourceId: String(party.id) },
                ];
                party.contact = true;
                party.primary = parentOrgData?.primaryContactId === contact.id;
                parentOrg.contacts.push(party);
              } else if (contact.customer) {
                // personal customer
                party.datasources = [
                  { source: 'weclapp', sourceId: String(party.id) },
                ];
                resultOrganizations.push(party);
                existingPersons.push(party);
              }
            } else {
              if (!existing.datasources) existing.datasources = [];
              // Skip if person already has a different weclapp datasource
              if (
                existing.datasources.some(
                  (ds) =>
                    ds.source === 'weclapp' && ds.sourceId !== String(party.id),
                )
              ) {
                continue;
              }
              // Add weclapp datasource if not present
              if (
                !existing.datasources.some(
                  (ds) =>
                    ds.source === 'weclapp' && ds.sourceId == String(party.id),
                )
              ) {
                existing.datasources.push({
                  source: 'weclapp',
                  sourceId: String(party.id),
                  sourceType: 'company',
                });
              }
              if (
                sourceOrder.filter((source) =>
                  existing.datasources.some((ds) => ds.source === source),
                )[0] === 'weclapp'
              ) {
                // Overwrite with weclapp data if weclapp is the first source
                if (!contact.customer) {
                  const merged = mergeParties(existing, party);
                  Object.assign(existing, merged);
                } else {
                  resultOrganizations.push(mergeParties(existing, party));
                }
              }
            }
            updateProcessingProgress();
          }
        }

        if (source === 'tanss') {
          /*
           * TANSS Companies
           */
          for (const company of tanssCompanies) {
            if (!company.displayId) {
              updateProcessingProgress();
              continue;
            }
            const {
              data: { content: fullCompany },
            } = await retry(() => proxy.get(`/${INTERFACE_STORE.tanss}/companies/${company.id}`));

            const isCustomer =
              fullCompany?.types?.some((t) => t.id === 1) || false;
            const isSupplier =
              fullCompany?.types?.some((t) => t.id === 2) || false;
            const isLead = fullCompany?.types?.some((t) => t.id === 3) || false;

            if (
              excludedIds.includes(String(fullCompany.headquarterId)) ||
              fullCompany.personalCustomer ||
              (!isCustomer && !isSupplier && !isLead)
            ) {
              excludedIds.push(String(fullCompany.id));
              continue;
            }

            let existing = resultOrganizations.find(
              (item) =>
                ((isCustomer || isLead) &&
                  item.customerNumber === company.displayId) ||
                (isSupplier && item.supplierNumber === company.displayId),
            );
            const party = transformToInternalSchema('tanss', {
              ...fullCompany,
              partyType: 'ORGANIZATION',
            });
            if (!existing) {
              party.datasources = [
                {
                  source: 'tanss',
                  sourceId: String(party.id),
                  sourceType: 'company',
                },
              ];
              party.contacts = [];
              resultOrganizations.push(party);
            } else {
              if (!existing.datasources) existing.datasources = [];
              if (
                !existing.datasources.some(
                  (ds) =>
                    ds.source === 'tanss' &&
                    ds.sourceId == String(fullCompany.id),
                )
              ) {
                existing.datasources.push({
                  source: 'tanss',
                  sourceId: String(fullCompany.id),
                  sourceType: 'company',
                });
              }
              if (
                sourceOrder.filter((source) =>
                  existing.datasources.some((ds) => ds.source === source),
                )[0] === 'tanss'
              ) {
                // Overwrite with Tanss data if Tanss is the first source
                const merged = mergeParties(existing, party);
                Object.assign(existing, merged);
              }
              if (!existing.contacts) existing.contacts = [];
            }
            await new Promise((r) => setTimeout(r, 20)); // avoid rate limits
            updateProcessingProgress();
          }
          /*
           * TANSS Employees
           */
          for (const companyId in tanssEmployeeMap) {
            for (const employee of tanssEmployeeMap[companyId]) {
              if (!employee.emailAddress) {
                updateProcessingProgress();
                continue;
              }
              const party = transformToInternalSchema('tanss', {
                ...employee,
                partyType: 'PERSON',
              });
              let existing =
                resultOrganizations
                  .map((o) => o.contacts)
                  .flat()
                  .find((p) => p.email === party.email) ||
                existingPersons.find(
                  (p) =>
                    p.email === party.email &&
                    p.partyType === 'PERSON' &&
                    p.customer,
                );
              const parentOrg = findParentOrg('tanss', companyId);
              if (!existing) {
                if (parentOrg) {
                  party.datasources = [
                    {
                      source: 'tanss',
                      sourceId: String(party.id),
                      sourceType: 'employee',
                    },
                  ];
                  const assignment = employee.companyAssignments?.find(
                    (a) => String(a.companyId) == String(companyId),
                  );
                  party.contact = assignment?.contactPerson || false;
                  party.primary = false;
                  parentOrg.contacts.push(party);
                  if (party.customer) {
                    existingPersons.push(party);
                  }
                }
              } else {
                if (!existing.datasources) existing.datasources = [];
                // Skip if person already has Tanss datasource
                if (
                  existing.datasources.some(
                    (ds) =>
                      ds.source === 'tanss' && ds.sourceId !== String(party.id),
                  )
                ) {
                  continue;
                }
                // Add Tanss datasource if not present
                if (
                  !existing.datasources.some(
                    (ds) =>
                      ds.source === 'tanss' && ds.sourceId == String(party.id) && ds.sourceType === 'employee',
                  )
                ) {
                  existing.datasources.push({
                    source: 'tanss',
                    sourceId: String(party.id),
                    sourceType: 'employee',
                  });
                }
                if (
                  sourceOrder.filter((source) =>
                    existing.datasources.some((ds) => ds.source === source),
                  )[0] === 'tanss'
                ) {
                  if (!party.customer) {
                    const merged = mergeParties(existing, party);
                    Object.assign(existing, merged);
                  } else {
                    resultOrganizations.push(mergeParties(existing, party));
                  }
                }
              }
              updateProcessingProgress();
            }
          }
        }
      }

      const totalPersons = resultOrganizations.reduce(
        (sum, org) => sum + (org.contacts?.length || 0),
        0,
      );
      log(
        `Daten erfolgreich geladen: ${resultOrganizations.length} Organisationen und Privatkunden sowie ${totalPersons} Personen zugeordnet`,
      );

      console.log({ resultOrganizations }); // For debugging

      setLocalResult(resultOrganizations);
      setResult(resultOrganizations);
      setProgress(100);
    } catch (error) {
      log(`Fehler beim Laden der Daten: ${error.message}`);
    }
  }

  useEffect(() => {
    if (!result) loadData();
    else setResult(result);
  }, []);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        px: '20px',
      }}
    >
      <ProgressIndicator progress={progress} logs={logs} />
    </Box>
  );
};

export default PartyLoader;
